import { Button, Checkbox, CustomModal, LoadingSpinner } from '@components/tailwind';
import { CustomModalRef } from '@components/tailwind/CustomModal';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import PersonalizationItem from './PersonalizationItem';
import ActiveVariant from './ActiveVariant';
import ItemPricing from './ItemPricing';
import ProductAvailability from '@components/tailwind/ProductAvailability';
import { Views } from '..';
import { getPersonalizationBreakdown } from './ItemInfo';
import { formatCurrency } from '@utils/formatters.util';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { Breakpoints } from '@settings';

type Props = {
  className?: string;
  title: string | React.ReactNode;
  faces: any;
  elementPrice: any;
  facePrices: any;
  onAddPersonalizationClick: any;
  config: any;
  onEditElement: any;
  onRemoveElement: any;
  onSetActiveGroup: any;
  onSummaryModalOpen?: () => void;
  personalizationPrice: number;
  processingDays: number;
  elementsToCharge: any;
  premiumElements: any;
  setView: (view: Views) => void;
  price: number;
  quantity: number;
  quantityRef: any;
  isAddToCartDisabled: boolean;
  onAddToCartSubmit: () => void;
  addToCartText: string;
  canExport: boolean;
  acceptTerms: boolean;
  setAcceptTerms: (acceptTerms: boolean) => void;
};

type ForwardedRefs = {
  openModal: () => void;
  closeModal: () => void;
};

const PersonalizationSummaryModal: React.ForwardRefRenderFunction<ForwardedRefs, Props> = (
  {
    className = '',
    title,
    config,
    faces,
    elementPrice,
    facePrices,
    onAddPersonalizationClick,
    onEditElement,
    onRemoveElement,
    onSetActiveGroup,
    personalizationPrice,
    processingDays,
    elementsToCharge,
    premiumElements,
    setView,
    price,
    quantity,
    quantityRef,
    isAddToCartDisabled,
    onAddToCartSubmit,
    addToCartText,
    canExport,
    acceptTerms,
    setAcceptTerms,
  },
  forwardedRef
) => {
  const { details } = useSelector((state: any) => state.pdpItem);
  const customModalRef = useRef<CustomModalRef>(null);
  const [loading, setLoading] = useState(false);
  const isTablet = useMediaQuery({ maxWidth: Breakpoints.LG });

  useImperativeHandle(
    forwardedRef,
    () => ({
      openModal: customModalRef.current.openModal,
      closeModal: customModalRef.current.closeModal,
    }),
    [customModalRef]
  );

  const isGroupOrder = !!config
    .reduce((acc, c) => [...acc, ...(faces.find((f) => f.key === c.face)?.elements ?? [])], [])
    .find((e) => e.isGroup);

  return (
    <CustomModal
      ref={customModalRef}
      id='PersonalizationSummaryModal'
      title={title}
      size='xl'
      defaultShown={false}
      dialogClassName='!tw-rounded-none lg:!tw-rounded-lg !tw-min-h-[100dvh] lg:tw-min-h-0'
      className={classNames('!tw-h-auto !tw-pt-0 lg:!tw-px-4 lg:!tw-py-20', className)}
      titleClassName='tw-shadow-[0px_3px_6px_rgba(0,0,0,0.2)] lg:tw-shadow-none'
      hideCloseButton={isTablet}>
      {loading && <LoadingSpinner small />}
      <div className='tw-mx-auto tw-max-w-[955px]'>
        <div>
          {config.map((c) => (
            <PersonalizationItem
              key={c.face}
              faceName={c.face}
              showAllGroupOrderElements
              elements={faces.find((f) => f.key === c.face)?.elements}
              totalPrice={quantity * parseFloat((facePrices[c.face] ?? 0).toFixed(2))}
              showTitleIcon={c.face === 'front' || c.face === 'logo'}
              onAddPersonalizationClick={onAddPersonalizationClick(c)}
              {...{
                elementPrice: elementPrice * quantity,
                onRemoveElement,
                onEditElement,
                onSetActiveGroup,
                isGroupOrder,
                quantity,
              }}
            />
          ))}
        </div>

        <>
          <ActiveVariant
            onUpdateClick={() => setView(Views.UPDATE_VARIANT)}
            view={Views.REVIEW}
            quantity={quantity}
          />

          <div className='tw-w-full tw-border-b tw-border-gray-300'>
            <ItemPricing
              personalizationFees={personalizationPrice}
              personalizationBreakdown={getPersonalizationBreakdown(
                facePrices,
                elementPrice,
                elementsToCharge,
                premiumElements
              )}
              quantity={quantity}
            />
          </div>

          <div className='tw-mt-4 tw-flex tw-items-center tw-justify-between tw-gap-2'>
            <div className='tw-max-w-[230px]'>
              {processingDays > 0 && (
                <ProductAvailability
                  quantityPerOrderLimit={details?.quantityPerOrderLimit}
                  stock={details?.stock}
                  processingDays={processingDays}
                />
              )}
            </div>
            <p className='tw-mb-0 tw-inline-block tw-text-[28px] tw-font-bold tw-text-brand-black'>
              {personalizationPrice > 0 && formatCurrency(price * quantity)}
            </p>
          </div>
        </>

        <div className='tw-absolute tw-bottom-5 tw-left-0 tw-right-0 tw-mx-auto tw-px-4 tw-pt-3 tw-text-center tw-shadow-[0px_-6px_6px_rgba(0,0,0,0.15)] tw-pb-safe lg:tw-static lg:tw-mt-5 lg:tw-max-w-[295px] lg:tw-px-0 lg:tw-pb-0 lg:tw-pt-0 lg:tw-shadow-none'>
          <div className='tw-mb-2 tw-text-center lg:tw-mb-4'>
            <span className='tw-mx-auto tw-mt-3 tw-max-w-[295px] tw-text-2xs tw-font-normal tw-text-gray-dark lg:tw-text-[13px]'>
              By proceeding with personalization, you are agreeing to{' '}
              <span
                className='tw-cursor-pointer tw-text-brand-black tw-underline tw-transition tw-duration-200 hover:tw-opacity-75'
                onClick={() => setView(Views.TERMS)}>
                MyRTIC Terms & Conditions.
              </span>
            </span>
          </div>

          <Button
            disabled={isAddToCartDisabled}
            variant='primary'
            fullWidth='all'
            size='md'
            onClick={() => {
              onAddToCartSubmit();
              setLoading(true);
            }}>
            {addToCartText}
          </Button>

          {!canExport && (
            <div className='tw-mb-1 tw-mt-1 tw-text-center tw-text-2xs tw-text-red-700'>
              There was a problem generating the required files.
            </div>
          )}

          <div className='tw-mt-4 tw-text-center tw-text-sm'>
            <Button noPadding size='md' variant='link' onClick={() => customModalRef.current.closeModal()}>
              CANCEL
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

const ForwardedPersonalizationSummaryModal = forwardRef(PersonalizationSummaryModal);
export type PersonalizationSummaryModalRef = React.ElementRef<typeof ForwardedPersonalizationSummaryModal>;

export default ForwardedPersonalizationSummaryModal;
